import { useState, useEffect, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.tsx'
import { Button } from './ui/button.tsx'
import { Avatar, AvatarFallback } from './ui/avatar.tsx'
import { Sheet, SheetContent } from './ui/sheet.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx'
import {
  Menu, Car, Smartphone, ScrollText, Zap, Newspaper,
  LogOut, Search, ChevronDown, Headphones, Send,
  User, Layers, Bell, AlertTriangle, LayoutDashboard, Download, Loader2, Terminal, Home, KeyRound
} from 'lucide-react'
import { api, type Build } from '../api/client.ts'
import { cn } from '../lib/utils.ts'

interface NavItem {
  path: string
  label: string
  icon: React.ElementType
  keywords?: string[]
}

interface NavGroup {
  title: string
  items: NavItem[]
}

/* Навигация разбита на два раздела: повседневная работа и служебное */
const navGroups: NavGroup[] = [
  {
    title: 'Главное',
    items: [
      { path: '/cars', label: 'Автомобили', icon: Car, keywords: ['cars', 'car', 'vehicles', 'vehicle', 'vin', 'drivers', 'авто', 'машины', 'транспорт'] },
      { path: '/dashboard', label: 'Дашборд', icon: LayoutDashboard, keywords: ['dashboard', 'dash', 'main', 'главная', 'домой'] },
      { path: '/activations', label: 'Активации', icon: Zap, keywords: ['activations', 'activation', 'активации', 'лицензии'] },
      { path: '/devices', label: 'Устройства', icon: Smartphone, keywords: ['devices', 'device', 'dev', 'smartphone', 'phone', 'устройства', 'телефоны', 'гаджеты'] },
      { path: '/users', label: 'Пользователи', icon: User, keywords: ['users', 'user', 'accounts', 'account', 'people', 'drivers', 'пользователи', 'юзеры', 'водители'] },
      { path: '/overlays', label: 'Оверлеи', icon: Layers, keywords: ['overlays', 'overlay', 'оверлеи', 'шаблоны'] },
      { path: '/car-assets', label: 'Ассеты моделей', icon: Layers, keywords: ['car assets', 'car-assets', 'ds05', 'deepal', 'minio', 'ассеты', 'модели'] },
      { path: '/builds', label: 'Сборки', icon: Download, keywords: ['builds', 'build', 'releases', 'activator', 'сборки', 'версии', 'скачать'] },
      { path: '/progress', label: 'Прогресс по моделям', icon: Zap, keywords: ['progress', 'progress', 'русификация', 'прогресс', 'модели', 'этапы'] },
      { path: '/client/news', label: 'Новости', icon: Newspaper, keywords: ['news', 'новости'] },
    ],
  },
  {
    title: 'Личный кабинет',
    items: [
      { path: '/client', label: 'Мой гараж', icon: Home, keywords: ['client', 'garage', 'гараж', 'кабинет'] },
      { path: '/progress', label: 'Прогресс по моделям', icon: Zap, keywords: ['progress', 'progress', 'русификация', 'прогресс', 'модели', 'этапы'] },
      { path: '/client/news', label: 'Новости', icon: Newspaper, keywords: ['news', 'новости'] },
    ],
  },
  {
    title: 'Система',
    items: [
      { path: '/logs', label: 'Логи', icon: ScrollText, keywords: ['logs', 'log', 'events', 'логи', 'журнал'] },
      { path: '/news', label: 'Новости', icon: Newspaper, keywords: ['news', 'posts', 'новости'] },
    ],
  },
  {
    title: 'Администрирование',
    items: [
      { path: '/adb', label: 'ADB', icon: Terminal, keywords: ['adb', 'deepal', 's05', 'remote', 'доступ', 'терминал'] },
      { path: '/q07-adb', label: 'ADB через сервер', icon: KeyRound, keywords: ['q07', 'adb', 'через сервер', 'код', 'changan', 'sda', 'engineering', 'инженерный'] },
    ],
  },
]

interface SidebarNavProps {
  groups: NavGroup[]
  className?: string
  onClick?: () => void
}

function SidebarNav({ groups, className, onClick }: SidebarNavProps) {
  const location = useLocation()

  /* Активный пункт держится яркостью, а не цветом: залитый акцентом фон
     вместе с цветным текстом перегружал меню и спорил с логотипом.
     Цвет остаётся только в полосе слева */
  const linkClass = (active: boolean) =>
    cn(
      "relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm",
      active
        ? "font-semibold text-[var(--figma-text)] bg-[var(--figma-surface-2)]"
        : "font-medium text-[var(--figma-text-muted)] hover:text-[var(--figma-text)] hover:bg-[var(--figma-surface-2)]"
    )

  /* '/' — это редирект на /cars (см. App.tsx), Дашборд живёт на /dashboard,
     так что подсветка активного пункта — обычное точное совпадение пути */
  const isActive = (path: string) => location.pathname === path

  return (
    <nav className={cn("flex flex-col", className)}>
      {groups.map((group, gi) => (
        <div
          key={group.title}
          className="flex flex-col gap-1"
          style={
            /* Разделы отделяются линией и воздухом, а не только заголовком:
               иначе «Система» читается продолжением «Главного» */
            gi > 0
              ? { marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--figma-border-subtle)' }
              : undefined
          }
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--figma-text-dim)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              padding: '0 12px 8px',
            }}
          >
            {group.title}
          </div>
          {group.items.map((item) => {
            const Icon = item.icon
            const active = isActive(item.path)
            return (
              <Link key={item.path} to={item.path} onClick={onClick} className={linkClass(active)}>
                {active && (
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute', left: 0, width: 3, height: 20,
                      background: 'var(--figma-accent)', borderRadius: 2,
                    }}
                  />
                )}
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            )
          })}
        </div>
      ))}
    </nav>
  )
}

interface LayoutProps {
  children: ReactNode
}

/* Едва заметный код-паттерн на фоне сайдбара — тайлится поверх лёгкого
   диагонального градиента. Держим opacity низким одной ручкой на <g>,
   а не на каждом глифе, чтобы легко подкрутить яркость в одном месте. */
const SIDEBAR_CODE_PATTERN = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'>
  <g fill='#4a9ee5' font-family='ui-monospace,SFMono-Regular,Menlo,monospace' opacity='0.09'>
    <text x='14' y='28' font-size='14'>&lt;/&gt;</text>
    <text x='125' y='18' font-size='11'>01</text>
    <text x='30' y='76' font-size='12'>{ }</text>
    <text x='150' y='96' font-size='12'>=&gt;</text>
    <text x='68' y='132' font-size='11'>&amp;&amp;</text>
    <text x='8' y='176' font-size='12'>10</text>
    <text x='142' y='168' font-size='13'>[ ]</text>
    <text x='96' y='48' font-size='10'>;</text>
  </g>
</svg>`

const sidebarBackgroundStyle = {
  backgroundImage:
    `linear-gradient(165deg, rgba(26,32,52,0.94) 0%, rgba(16,20,36,0.92) 48%, rgba(8,10,20,0.95) 100%), ` +
    `url("data:image/svg+xml,${encodeURIComponent(SIDEBAR_CODE_PATTERN)}")`,
  backgroundSize: 'auto, 200px 200px',
  backgroundRepeat: 'no-repeat, repeat',
  borderColor: 'var(--figma-border)',
} as const

function ActivatorDownloadButton({ onClick, isClient = false }: { onClick?: () => void; isClient?: boolean }) {
  const [build, setBuild] = useState<Build | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api.builds.latest('stable')
      .then((response: any) => {
        if (cancelled) return
        const latest = response.data as Build | undefined
        setBuild(latest?.download_url ? latest : null)
      })
      .catch(() => { if (!cancelled) setBuild(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  if (!build?.download_url) {
    return (
      <div
        className="w-full min-h-11 rounded-[10px] px-3 py-2 flex items-center gap-2.5"
        style={{ background: 'var(--figma-surface-3)', color: 'var(--figma-text-muted)', border: '1px solid var(--figma-border)' }}
        aria-live="polite"
      >
        <div className="h-8 w-8 rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'var(--figma-surface-2)' }}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </div>
        <div className="min-w-0 text-left">
          <div className="text-[12.5px] font-semibold leading-tight">Скачать активатор</div>
          <div className="text-[10.5px] leading-tight mt-0.5 truncate">
            {loading ? 'Ищем актуальную версию…' : 'Актуальная сборка недоступна'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <a
      href={build.download_url}
      target="drivebox-build-download"
      download
      onClick={onClick}
      className="w-full min-h-12 rounded-[10px] px-3 py-2.5 flex items-center gap-2.5 no-underline transition-all hover:brightness-110 hover:-translate-y-px"
      style={{
        background: 'linear-gradient(135deg, rgba(0,180,232,0.24), rgba(0,114,184,0.16))',
        color: 'var(--figma-text)',
        border: '1px solid rgba(0,180,232,0.48)',
        boxShadow: '0 6px 20px rgba(0,114,184,0.14)',
      }}
      aria-label={`Скачать активатор версии ${build.version}`}
    >
      <div className="h-8 w-8 rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#00b4e8,#0072b8)', color: '#fff' }}>
        <Download className="h-4 w-4" />
      </div>
      <div className="min-w-0 text-left">
        <div className="flex items-center gap-1.5 leading-tight">
          <span className="text-[12.5px] font-semibold">Скачать активатор</span>
          <span className="rounded px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em]" style={{ color: '#71d7f5', background: 'rgba(0,180,232,0.14)' }}>
            актуальная
          </span>
        </div>
        <div className="text-[10.5px] leading-tight mt-1 truncate" style={{ color: 'var(--figma-text-muted)' }}>
          v{build.version}{isClient ? ' · для личного кабинета' : (build.partner ? ` · ${build.partner}` : ' · публичная')}
        </div>
      </div>
    </a>
  )
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout, isAdmin, isModerator, isPartner, isClient, canManageCars, canViewLogs, canManageNews } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    const wasCabinetUser = isClient || isPartner
    await logout()
    // Client and partner accounts enter through the shared cabinet. Return
    // them there after the admin-domain session has been cleared; the local
    // /login route remains exclusively for staff.
    if (wasCabinetUser) {
      window.location.href = 'https://drivebox.pro/cabinet'
      return
    }
    navigate('/login')
  }

	const handleStopImpersonating = async () => {
		await api.users.stopImpersonating()
		window.location.href = '/users'
	}

  const roleLabels: Record<string, string> = {
    admin: 'Администратор',
    moderator: 'Модератор',
    partner: 'Партнёр',
    client: 'Клиент',
  }
  const roleLabel = roleLabels[user?.role || ''] || 'Пользователь'

  const [notifications, setNotifications] = useState<any[]>([])
  const [notifOpen, setNotifOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.logs.list(20, 0, '')
      .then((res: any) => {
        if (cancelled) return
        const logs = (res.data?.logs || []) as any[]
        setNotifications(
          logs
            .filter((l: any) =>
              ['error', 'warning', 'critical', 'alert'].includes((l.severity || '').toLowerCase())
            )
            .slice(0, 5)
        )
      })
      .catch(() => { if (!cancelled) setNotifications([]) })
    return () => { cancelled = true }
  }, [])

  const canViewItem = (path: string, groupTitle: string) => {
    // /progress and /client/news are each listed under both groups so every
    // role sees them grouped with the rest of what's relevant to them: a
    // partner works alongside staff day-to-day, so their copy belongs in
    // Главное next to Автомобили/Сборки/etc, not tucked away in a
    // partner-only "cabinet" section that would just look like extra,
    // separate work. A client has no Главное at all, so theirs is always
    // the Личный кабинет copy. Resolve by group first, so a role never
    // sees either item twice.
    if (path === '/progress' || path === '/client/news') {
      if (groupTitle === 'Личный кабинет') return isClient
      // Главное copy: /progress already belonged to admin/moderator here —
      // partner's own copy of it now lives here too instead of a separate
      // "cabinet" section. /client/news is new to this group and has no
      // admin/moderator use here (they already manage their own news feed
      // under Система) — partner-only.
      return path === '/progress' ? (isAdmin || isModerator || isPartner) : isPartner
    }
    // "Мой гараж" (own vehicles, paired via a one-time code from the
    // installer) is a client concept only - a partner already manages cars
    // at scale under Автомобили/Активации, a personal garage would just be
    // confusing duplication for them, not a superset of what they can
    // already do. Checked before the admin/moderator blanket rules below on
    // purpose, since admin already sees the real thing too.
    if (path === '/client' || path.startsWith('/client/')) return isClient
    if (isAdmin) return true
    if (isClient) return false
    if (isPartner) return path === '/dashboard' || path === '/cars' || path === '/devices' || path === '/activations' || path === '/builds'
    if (path === '/dashboard') return true
    if (path === '/news') return canManageNews
    if (path === '/cars') return canManageCars
    if (path === '/devices') return isModerator
    if (path === '/logs') return canViewLogs
    if (path === '/activations') return isModerator
    if (path === '/users') return isAdmin
    if (path === '/overlays') return isAdmin
    if (path === '/car-assets') return isAdmin
    if (path === '/builds') return isModerator
    if (path === '/adb') return isAdmin
    if (path === '/q07-adb') return isAdmin
    return false
  }

  /* Фильтруем внутри групп и убираем те, что опустели: иначе у роли
     без доступа остался бы висеть заголовок раздела без пунктов */
  const visibleNavGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => canViewItem(item.path, group.title)) }))
    .filter((group) => group.items.length > 0)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const searchTerm = searchQuery.trim().toLowerCase()
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchLoading, setSearchLoading] = useState(false)

  const iconFor = (type: string) => {
    switch (type) {
      case 'car': return Car
      case 'device': return Smartphone
      case 'activation': return Zap
      case 'user': return User
      case 'hwid':
      case 'partner_hwid': return Headphones
      default: return Search
    }
  }

  useEffect(() => {
    if (searchTerm.length < 3) {
      setSearchResults([])
      setSearchLoading(false)
      return
    }
    setSearchLoading(true)
    const timer = setTimeout(() => {
      api.search(searchTerm)
        .then((res: any) => {
          setSearchResults(Array.isArray(res?.data?.results) ? res.data.results : [])
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false))
    }, 200)
    return () => clearTimeout(timer)
  }, [searchTerm])

  return (
    <div className="min-h-screen text-foreground flex" style={{ background: 'var(--figma-bg-gradient)' }}>
      {/* Same faint grid used on the login/cabinet screens (drivebox.pro/cabinet
          included), so the app doesn't visually change character the moment
          you're past the login form. Fixed rather than absolute - login pages
          sit in one static viewport, this one scrolls, and the grid should
          stay put as a backdrop rather than scroll away with the content. */}
      <style>{`
        .app-grid-bg {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background-image:
            linear-gradient(to right, rgba(255,255,255,0.022) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.022) 1px, transparent 1px);
          background-size: 44px 44px;
          mask-image: radial-gradient(70% 55% at 50% -10%, #000 0%, transparent 100%);
          -webkit-mask-image: radial-gradient(70% 55% at 50% -10%, #000 0%, transparent 100%);
        }
      `}</style>
      <div className="app-grid-bg" />
      {/* Download into a separate browsing context so the SPA stays open. */}
      <iframe
        name="drivebox-build-download"
        title="Загрузка сборки"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      />
      {/* Top Header Bar (full width) */}
      <header className="h-14 border-b flex items-center fixed top-0 inset-x-0 z-40 backdrop-blur-xl" style={{ background: 'rgba(17,21,37,0.85)', borderColor: 'var(--figma-border)' }}>
        {/* Logo slot — same width as sidebar */}
        <div className="hidden md:flex items-center gap-1.5 w-[260px] px-5 border-r h-full flex-shrink-0" style={{ borderColor: 'var(--figma-border)' }}>
          <img
            src={`${import.meta.env.BASE_URL}drivebox-mark.svg`}
            alt="DriveBox"
            className="h-7 w-7 flex-shrink-0 object-contain"
          />
          <span className="font-bold text-[15px] tracking-wide truncate" style={{ color: 'var(--figma-text)' }}>
            Drive<span style={{ color: 'var(--figma-accent)' }}>Box</span>{!isClient && ' Activator'}
          </span>
        </div>

        {/* Mobile logo + hamburger */}
        <div className="md:hidden flex items-center gap-2.5 px-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 -ml-2"
            aria-label="Открыть меню"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <img src={`${import.meta.env.BASE_URL}drivebox-mark.svg`} alt="" className="h-7 w-7 object-contain" />
          <span className="font-bold text-sm tracking-wide" style={{ color: 'var(--figma-text)' }}>Drive<span style={{ color: 'var(--figma-accent)' }}>Box</span></span>
        </div>

        {/* Search — left aligned right after logo. A client has nothing this
            searches over (activator data), so it's just an empty spacer for
            that role rather than a dead/confusing input. */}
        <div className="hidden md:flex flex-1 px-6">
          {isClient ? null : (
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Глобальный поиск"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true) }}
              onFocus={() => setSearchOpen(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setSearchOpen(false)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const first = searchResults[0]
                  if (first) {
                    e.preventDefault()
                    navigate(first.path)
                    setSearchQuery('')
                    setSearchOpen(false)
                  }
                } else if (e.key === 'Escape') {
                  setSearchOpen(false)
                }
              }}
              className="w-full h-9 pl-9 pr-4 rounded-lg bg-secondary/50 border border-input text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring transition-all"
            />
            {searchOpen && searchTerm.length >= 3 && (
              <div
                className="absolute left-0 right-0 top-full mt-2 rounded-lg border shadow-lg p-2 max-h-[60vh] overflow-y-auto"
                style={{ background: 'var(--figma-surface)', borderColor: 'var(--figma-border)', zIndex: 60 }}
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
              >
                {searchLoading ? (
                  <div className="px-3 py-2 text-sm" style={{ color: 'var(--figma-text-muted)' }}>Поиск...</div>
                ) : searchResults.length === 0 ? (
                  <div className="px-3 py-2 text-sm" style={{ color: 'var(--figma-text-muted)' }}>Ничего не найдено</div>
                ) : (
                  searchResults.map((item: any) => {
                    const Icon = iconFor(item.type)
                    return (
                      <button
                        key={`${item.type}-${item.id}-${item.url}`}
                        type="button"
                        onClick={() => {
                          navigate(item.url)
                          setSearchQuery('')
                          setSearchOpen(false)
                        }}
                        className="w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-3 hover:bg-white/[0.05] transition-colors"
                        style={{ color: 'var(--figma-text)' }}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--figma-text-muted)' }} />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-xs truncate" style={{ color: 'var(--figma-text-muted)' }}>{item.subtitle}</div>
                          )}
                        </div>
                        <span className="text-xs capitalize" style={{ color: 'var(--figma-text-muted)' }}>{item.type}</span>
                      </button>
                    )
                  })
                )}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Right: Notifications + User */}
        <div className="flex items-center gap-2 px-4 md:px-6 ml-auto">
          {!isClient && (
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-9 w-9 hover:bg-accent">
                <Bell className="h-4 w-4" style={{ color: 'var(--figma-text-muted)' }} />
                {notifications.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: 'var(--figma-red)', color: '#fff' }}>
                    {notifications.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[320px] p-0 overflow-hidden" style={{ background: 'var(--figma-surface)', borderColor: 'var(--figma-border)', maxWidth: 'calc(100vw - 2rem)' }}>
              <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--figma-border)' }}>
                <span className="text-sm font-semibold" style={{ color: 'var(--figma-text)' }}>Уведомления</span>
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-3 text-sm" style={{ color: 'var(--figma-text-muted)' }}>Нет важных уведомлений</div>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  {notifications.map((n, i) => (
                    <div key={i} className="px-4 py-3 border-b last:border-b-0 flex items-start gap-3" style={{ borderColor: 'var(--figma-border-subtle)' }}>
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--figma-yellow)' }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" style={{ color: 'var(--figma-text)' }}>{n.message || n.endpoint || 'Событие'}</div>
                        <div className="text-[11px] truncate" style={{ color: 'var(--figma-text-muted)' }}>{n.severity || 'warning'}{n.ip ? ` • ${n.ip}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 pl-2 pr-1 flex items-center gap-2 hover:bg-accent">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                    {user?.username?.slice(0, 1).toUpperCase() || 'A'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col items-start leading-none">
                  <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--figma-text)' }}>
                    {roleLabel}
                    <ChevronDown className="h-3 w-3" style={{ color: 'var(--figma-text-muted)' }} />
                  </span>
                  <span className="text-[10px] mt-0.5" style={{ color: 'var(--figma-text-muted)' }}>{user?.username || 'admin'}</span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[280px] p-0 overflow-hidden" style={{ background: 'var(--figma-surface)', borderColor: 'var(--figma-border)', maxWidth: 'calc(100vw - 2rem)' }}>
              {/* Profile header */}
              <div className="px-4 py-3.5 border-b" style={{ borderColor: 'var(--figma-border)' }}>
                <div className="flex items-center gap-3">
                  <Avatar className="h-11 w-11">
                    <AvatarFallback className="bg-primary/15 text-primary text-base font-bold">
                      {user?.username?.slice(0, 1).toUpperCase() || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-[15px] font-semibold truncate" style={{ color: 'var(--figma-text)' }}>{user?.username || 'admin'}</span>
                    <span
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full self-start"
                      style={{ background: 'var(--figma-surface-3)', color: 'var(--figma-text)' }}
                    >
                      {roleLabel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Logout */}
              <div className="border-t px-2 py-2" style={{ borderColor: 'var(--figma-border)' }}>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[8px] text-[13.5px] font-medium transition-colors"
                  style={{ color: 'var(--figma-red)' }}
                >
                  <LogOut className="h-4 w-4" />
                  Выйти из аккаунта
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Sidebar (below header) */}
      <aside className="w-[260px] hidden md:flex flex-col border-r fixed top-14 bottom-0 z-30 backdrop-blur-xl" style={sidebarBackgroundStyle}>
        <div className="flex-1 py-3 px-3 overflow-y-auto">
          <SidebarNav groups={visibleNavGroups} />
        </div>
        <div className="p-3 space-y-2">
          <ActivatorDownloadButton isClient={isClient} />

          {/* Support card */}
          <div
            className="rounded-[12px] p-3"
            style={{ background: 'var(--figma-surface-2)', border: '1px solid var(--figma-border)' }}
          >
            <div className="flex items-center gap-2.5 mb-2.5">
              <div
                className="h-9 w-9 rounded-[9px] flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--figma-surface-3)' }}
              >
                <Headphones className="h-4.5 w-4.5" style={{ color: 'var(--figma-text-muted)', width: 18, height: 18 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold leading-tight">Поддержка</div>
                <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">Напишите нам в Telegram</div>
              </div>
            </div>
            <a
              href="https://t.me/godevelopment"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-9 rounded-[8px] flex items-center justify-center gap-2 text-[12.5px] font-semibold transition-colors no-underline"
              style={{ background: 'var(--figma-surface-3)', color: 'var(--figma-text-muted)', border: '1px solid var(--figma-border)' }}
            >
              <Send className="h-3.5 w-3.5" />
              Написать в Telegram
            </a>
          </div>

          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full h-9 rounded-[8px] flex items-center justify-center gap-2 text-[12.5px] text-slate-500 hover:text-slate-300 hover:bg-white/[0.03] transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Выйти ({user?.username || 'admin'})
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div className="md:hidden fixed top-14 left-0 z-30">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          {/* trigger moved to header */}
          <SheetContent side="left" className="w-[260px] max-w-[85vw] p-0" style={sidebarBackgroundStyle}>
            <div className="flex flex-col h-full">
              <div className="flex-1 py-3 px-3 overflow-y-auto">
                <SidebarNav groups={visibleNavGroups} />
              </div>
              <div className="p-3 space-y-2">
                <ActivatorDownloadButton isClient={isClient} onClick={() => setMobileNavOpen(false)} />

                <div
                  className="rounded-[12px] p-3"
                  style={{ background: 'var(--figma-surface-2)', border: '1px solid var(--figma-border)' }}
                >
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div
                      className="h-9 w-9 rounded-[9px] flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--figma-surface-3)' }}
                    >
                      <Headphones className="h-4.5 w-4.5" style={{ color: 'var(--figma-text-muted)', width: 18, height: 18 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold leading-tight">Поддержка</div>
                      <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">Напишите нам в Telegram</div>
                    </div>
                  </div>
                  <a
                    href="https://t.me/godevelopment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full h-9 rounded-[8px] flex items-center justify-center gap-2 text-[12.5px] font-semibold transition-colors no-underline"
                    style={{ background: 'var(--figma-surface-3)', color: 'var(--figma-text-muted)', border: '1px solid var(--figma-border)' }}
                  >
                    <Send className="h-3.5 w-3.5" />
                    Написать в Telegram
                  </a>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full h-9 rounded-[8px] flex items-center justify-center gap-2 text-[12.5px] text-slate-500 hover:text-slate-300 hover:bg-white/[0.03] transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Выйти ({user?.username || 'admin'})
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Main Content */}
      <main className="relative flex-1 md:ml-[260px] pt-14 min-h-screen">
        <div className="p-4 md:p-7 max-w-6xl mx-auto w-full">
		  {user?.impersonating && (
			<div className="mb-4 flex flex-col gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
			  <span className="text-sm text-amber-200">Вы просматриваете кабинет партнёра <strong>{user.username}</strong>.</span>
			  <button type="button" onClick={handleStopImpersonating} className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-400">
				Вернуться в администратора
			  </button>
			</div>
		  )}
          {children}
        </div>
      </main>
    </div>
  )
}
